<template>
  <span class="ui-text" :style="{ fontFamily, fontWeight }">
    <slot />
  </span>
</template>

<script setup>
import { computed, useSlots } from "vue";

import { useUserStoreHook } from "@/store/modules/user";

const props = defineProps({
  // title: 标题，bold: 粗体，light: 细体
  type: {
    type: String,
    default: "",
    validator: value => ["", "title", "bold", "light"].includes(value)
  },
  // 是否斜体，与 type 搭配使用
  italic: {
    type: Boolean,
    default: false
  }
});

const userStore = useUserStoreHook();
const slots = useSlots();

// 整段内容都是英文（允许空格、数字与英文标点）时按英文取字体，
// 避免中文、日文等语言下这类英文文案落到中日文字体
const ENGLISH_ONLY_REGEXP = /^[\x20-\x7E\s]+$/;

const collectSlotText = vnodes => {
  if (!Array.isArray(vnodes)) {
    return "";
  }

  return vnodes
    .map(vnode => {
      if (!vnode) {
        return "";
      }

      if (typeof vnode.children === "string") {
        return vnode.children;
      }

      return collectSlotText(vnode.children);
    })
    .join("");
};

// 内容语言：全英文内容按英文取字体，其余沿用当前语言
const fontLang = computed(() => {
  const text = collectSlotText(slots.default?.()).trim();

  return text && ENGLISH_ONLY_REGEXP.test(text) ? "en" : userStore.language;
});

// 常规黑体：英文 lato，日文 Noto Sans JP
const SANS_FONT_MAP = {
  en: "lato",
  jp: "NotoSansJP-Variable"
};
// 标题：英文 Cormorant Bold，日文 Noto Serif JP（宋体），其余语言沿用源宋 SemiBold
const TITLE_FONT_MAP = {
  en: "Cormorant-Bold",
  jp: '"NotoSerifJP-Variable", "NotoSerifJP-SemiBold"'
};
// 粗体与细体：英文用对应的 Lato 字重文件，日文用黑体（字重由父级控制），其余语言沿用源宋 SemiBold
const BOLD_FONT_MAP = {
  en: "lato-bold",
  jp: "NotoSansJP-Variable"
};
const LIGHT_FONT_MAP = {
  en: "lato-light",
  jp: "NotoSansJP-Variable"
};
// 斜体与粗/细斜体：只有英文有对应文件，其余语言没有斜体文件、保持正体
const ITALIC_FONT_MAP = {
  en: "lato-Italic"
};
const TITLE_ITALIC_FONT_MAP = {
  en: "Cormorant-BoldItalic"
};
const BOLD_ITALIC_FONT_MAP = {
  en: "lato-boldItalic"
};
const LIGHT_ITALIC_FONT_MAP = {
  en: "lato-lightItalic"
};
const DEFAULT_FONT = "var(--y-regular-font)";
const DEFAULT_SERIF_FONT = "SourceHanSerifTW-SemiBold";

// 本次渲染要用的斜体字体表：title 用 Cormorant 粗斜体，bold 用 Lato 粗斜体，其余类型用常规斜体
const italicFontMap = computed(() => {
  if (!props.italic) {
    return null;
  }

  switch (props.type) {
    case "light":
      return LIGHT_ITALIC_FONT_MAP;
    case "title":
      return TITLE_ITALIC_FONT_MAP;
    case "bold":
      return BOLD_ITALIC_FONT_MAP;
    default:
      return ITALIC_FONT_MAP;
  }
});

// 正体字体：按 type + 语言取
const baseFontFamily = computed(() => {
  const lang = fontLang.value;

  switch (props.type) {
    case "title":
      return TITLE_FONT_MAP[lang] || DEFAULT_SERIF_FONT;
    case "bold":
      return BOLD_FONT_MAP[lang] || DEFAULT_SERIF_FONT;
    case "light":
      return LIGHT_FONT_MAP[lang] || DEFAULT_SERIF_FONT;
    default:
      return SANS_FONT_MAP[lang] || DEFAULT_FONT;
  }
});

const fontFamily = computed(() => {
  const currentItalicFontMap = italicFontMap.value;
  if (!currentItalicFontMap) {
    return baseFontFamily.value;
  }

  // 没有斜体文件的语言（日文、中文）回退到同类型正体，保持不倾斜
  return currentItalicFontMap[fontLang.value] || baseFontFamily.value;
});

// 日文标题用宋体 Bold、粗体用黑体 Medium，其余语言与类型的字重仍由父级控制
const fontWeight = computed(() => {
  if (fontLang.value !== "jp") {
    return null;
  }

  switch (props.type) {
    case "title":
      return 700;
    case "bold":
      return 500;
    default:
      return null;
  }
});

defineOptions({
  name: "UiText"
});
</script>
<style lang="scss" scoped>
.ui-text {
  display: inline-block;
}
</style>
